import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import { toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import { callAddressType, createForm as createFormAddress } from "../../../../../components/address/address.logic";
import { setAppPageHeader } from "../../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../../i18n";
import router from "../../../../../router";
import store from "../../../../../store";
import {
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView,
    watchRulesChildren
} from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { has, have } from "../../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../../../utils/page";
import { flatWhereGroup } from "../../../../../utils/search";
import {
    email,
    hasIdCard,
    hasUsername,
    isAlphabetOrNumber,
    minLength, noHyphenValidator,
    required,
    requiredWhen,
    sameWhen,
    validateError
} from "../../../../../utils/validate";
import websocket from "../../../../../utils/websocket";
import { callPersonType } from "../../../../account/register/register.section";
import { createForm as createFormUser } from "../../../setting/user-mgt/user/user.logic";
import { mapLinkageGetData } from "../tab-1/tab1.logic";
import { loadRequestCheck } from "../tab-3/tab3.logic";
import { createConditionAction, createConditionForm } from "./tab4.condition";
import { callUserRequestDoc, callUserRequestDoc4Attachment, callUserRequestType, createColumns, createField, onLinkIdCard } from "./tab4.section";

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/userRequest";

export const callRequest = ({ form }) => store.dispatch(`${actionPrefix}/request`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

export const onSearch = ({ props, page, state, companyId }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...where, ...createWhereFor({ props, companyId })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ props, data, refs, formParams }) => {
    const { viewOnly, routerPrefix } = props;
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callRequest({ form: { ...data.useForm.modelRef, ...formParams } })
                .finally(() => setIsLoadingApp(false))
                .then(record => {
                    if (viewOnly) initForm({ props, data, refs });
                    else onClickView({ record, routerPrefix });
                });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ props, id, companyId, forLog }) => {
    if (!forLog) {
        return {
            select: ["*", "refUser.*", "refUserTemp.**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }, ...createWhereFor({ props, companyId })],
            children: [
                {
                    name: "refUserTemp.addresses",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                { name: "refUserTemp.attachments", select: ["*"], page: 0, size: 1000000, sort: [{ name: "createDate", type: "ASC" }] },
                {
                    name: "statuses",
                    asName: "lastStatus.forRequestRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "in", value: ["draft", "reEdit"] }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forOfficeRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["waitReceiveCustoms", "waitApproveCenter", "waitDocCustoms"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                }
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createForm = () => {
    return {
        model: null,
        refModelId: null,
        refModelTempId: null,
        refRequestType: null,
        refRequestDoc: null,
        refStatus: null,
        refDepartment: null,
        statusRemark: null,
        refUser: null,
        refUserTemp: { ...createFormUser(), addresses: [createFormAddress()] },
        statuses: [],
        lastStatus: {
            forRequestRemark: [{ remark: null, statusDatetime: null }],
            forOfficeRemark: [{ remark: null, statusDatetime: null }]
        }
    };
};

export const createRules = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refRequestType`]: [required],
        [`${prefix}refUser`]: [requiredWhen({ when: () => model.refRequestType?.key === "2" })],
        [`${prefix}refUserTemp.username`]: [
            {
                validator: (rule, value) => {
                    return required
                        .validator(rule, value)
                        .then(() => email.validator(rule, value))
                        .then(() => {
                            if (model.refRequestType?.key === "1") {
                                const isCheckDraft = true;
                                const idIgnore = model.refUserTemp?.id;
                                return hasUsername.validator(rule, value, isCheckDraft, idIgnore);
                            }
                            return Promise.resolve();
                        });
                },
                trigger: "blur"
            }
        ],
        [`${prefix}refUserTemp.password`]: [
            requiredWhen({
                when: () => {
                    const { useForm } = data;
                    const { modelRef } = useForm;
                    const model = key ? lodash.get(modelRef, key) : modelRef;
                    return model.refRequestType?.key === "1" ? !model.id : false;
                },
                requiredValidator: ({ rule, value }) => {
                    return required.validator(rule, value).then(() => minLength(4).validator(rule, value));
                }
            })
        ],
        [`${prefix}refUserTemp.passwordConfirm`]: [
            sameWhen({
                when: () => {
                    const { useForm } = data;
                    const { modelRef } = useForm;
                    const model = key ? lodash.get(modelRef, key) : modelRef;
                    return model.refUserTemp.password === model.refUserTemp.passwordConfirm;
                },
                keyLabel: i18n.global.t("model.user.password"),
                sameLabel: i18n.global.t("model.user.passwordConfirm")
            })
        ],
        [`${prefix}refUserTemp.refPersonType`]: [required],
        [`${prefix}refUserTemp.refDocumentTypePerson`]: [required],
        [`${prefix}refUserTemp.idCard`]: [
            required, noHyphenValidator(), isAlphabetOrNumber,
            {
                validator: (rule, value) => {
                    if (value && value.length !== 13) {
                        if (model.refUserTemp?.refDocumentTypePerson?.key === "1") {
                            return Promise.reject(i18n.global.t("error.form.equalsLength", { equals: 13 }));
                        }
                    }
                    return Promise.resolve();
                },
                trigger: "blur"
            },
            {
                ...requiredWhen({
                    when: () => model.refRequestType?.key === "1",
                    requiredValidator: ({ rule, value }) => {
                        return hasIdCard({ cardKey: () => model.refUserTemp.refDocumentTypePerson?.key }).validator(rule, value);
                    }
                }),
                trigger: "blur"
            }
        ],
        [`${prefix}refUserTemp.birthDate`]: [required],
        [`${prefix}refUserTemp.refNationality`]: [required],
        [`${prefix}refUserTemp.refRace`]: [required],
        [`${prefix}refUserTemp.refReligion`]: [required],
        [`${prefix}refUserTemp.refGender`]: [required],
        [`${prefix}refUserTemp.refTitle`]: [required],
        [`${prefix}refUserTemp.mobile`]: [required],
        [`${prefix}refUserTemp.firstNameTh`]: [required],
        [`${prefix}refUserTemp.firstNameEn`]: [required],
        [`${prefix}refUserTemp.lastNameTh`]: [required],
        [`${prefix}refUserTemp.lastNameEn`]: [required],
        [`${prefix}refUserTemp.addresses.0.refAddressType`]: [required],
        [`${prefix}refUserTemp.addresses.0.noTh`]: [required],
        [`${prefix}refUserTemp.addresses.0.noEn`]: [required],
        [`${prefix}refUserTemp.addresses.0.refProvince`]: [required],
        [`${prefix}refUserTemp.addresses.0.refAmphur`]: [required],
        [`${prefix}refUserTemp.addresses.0.refTambon`]: [required],
        [`${prefix}refUserTemp.addresses.0.postCode`]: [required],
        [`${prefix}lastStatus.forRequestRemark.0.remark`]: [requiredWhen({ when: () => !model.isOfficerCreate && model.refRequestType?.key === "2" && (!model.id || ["draft", "reEdit"].includes(model.refStatus?.key)) })]
    };
};

export const createRulesAttachment = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.refUserTemp?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refUserTemp.attachments.${index}.documentName`] = [required];
            rules[`${prefix}refUserTemp.attachments.${index}.size`] = [
                requiredWhen({ when: () => model.refUserTemp.attachments[index].attribute?.isRequiredAttachment })
            ];
        }
    });
    return rules;
};

export const createFormTitle = ({ data }) => {
    return data.useForm.modelRef.id ? data.useForm.modelRef.refUserTemp?.username : i18n.global.t("button.create");
};

export const createWhereFor = ({ props, companyId }) => {
    const { menuContext } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    if (has({ menuContext, key: "forRequest" })) {
        whereGroup.push([{ name: "refOwner.username", type: "String", operator: "=", value: userInfo.username }]);
    }
    if (have({ menuContext, keys: ["forCustoms", "forCenter"] })) {
        whereGroup.push([{ name: "refStatus.key", type: "String", operator: "!=", value: "draft" }]);
    }
    return [
        { name: "refOwner.id", type: "UUID", operator: "=", value: companyId },
        { name: "refRequestDoc.key", type: "String", operator: "=", value: "4" },
        ...flatWhereGroup({ where: whereGroup, logic: "OR" })
    ];
};

export const loadForm = ({ props, data, refs, state, companyId }) => {
    setIsLoading(true);
    const { form, routerPrefix } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, state, companyId, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page, routerPrefix }))
        .then(() => loadDefaultForm({ props, data, refs, routerPrefix }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] }, routerPrefix }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ props, data }) => {
    const { form, forceDefault } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    const promises = [callPersonType({}), callUserRequestType({}), callUserRequestDoc({}), callUserRequestDoc4Attachment({}), callAddressType({})];
    return Promise.all(promises).then(arr => {
        const userInfo = store.state.resource.auth.info?.user;
        data.personTypes = arr[0];
        data.userRequestTypes = arr[1];
        data.userRequestDocs = arr[2];
        data.userRequestDoc4Attachment = arr[3];
        data.addressTypes = arr[4];
        if ((!id && !form) || forceDefault) {
            modelRef.refOwner = userInfo;
            modelRef.refRequestType = data.userRequestTypes.find(d => d.key === "1");
            modelRef.refRequestDoc = data.userRequestDocs.find(d => d.key === "4");
            modelRef.refUserTemp.refPersonType = data.personTypes.find(d => d.key === "2");
            modelRef.refUserTemp.addresses[0].refAddressType = data.addressTypes.find(d => d.key === "1");
            data.userRequestDoc4Attachment.forEach(item =>
                modelRef.refUserTemp.attachments.push({ documentName: item.value, attribute: lodash.cloneDeep(item.attribute) })
            );
        }
    });
};

export const initState = ({ props, companyId }) => {
    const { menuContext, routerPrefix } = props;
    if (stateGroup[companyId]) {
        return stateGroup[companyId];
    }
    const state = {
        columns: createColumns(),
        page: createPage({
            options: {
                select: ["*", "refRequestType.*", "refStatus.*", "refUserTemp.*", "refUserTemp.refDocumentTypePerson.*"],
                sort: [{ name: "updateDate", type: "DESC" }],
                children: [
                    {
                        name: "statuses",
                        asName: "lastStatus.approve",
                        select: ["**"],
                        page: 0,
                        size: 1,
                        where: [
                            {
                                name: "refStatus.key",
                                type: "String",
                                operator: "=",
                                value: "approve"
                            }
                        ],
                        sort: [{ name: "createDate", type: "DESC" }]
                    }
                ]
            }
        }),
        tableOptions: { search: { activeKey: "Search" } },
        conditionAction: createConditionAction(),
        tabKey: "1",
        websocketId: null,
        websocketSubscribes: []
    };

    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.refUserTemp.fullNameTh).join(", ") }),
            onClick: ({ rowSelection }) =>
                onClickActiveAll({
                    rowSelection,
                    callActiveAll,
                    state,
                    onSearch: search => onSearch({ props, state, companyId, ...search }),
                    setIsLoading
                })
        }),
        createManageArchive({
            menuContext,
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.refUserTemp.fullNameTh).join(", "), okType: "danger" }),
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({
                    rowSelection,
                    callArchiveAll,
                    state,
                    onSearch: search => onSearch({ props, state, companyId, ...search }),
                    setIsLoading
                })
        }),
        createManageDelete({
            menuContext,
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.refUserTemp.fullNameTh).join(", "), okType: "danger" }),
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({
                    rowSelection,
                    callDeleteAll,
                    state,
                    onSearch: search => onSearch({ props, state, companyId, ...search }),
                    setIsLoading
                }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canDelete({ props, record }) })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        })
    ];
    return (stateGroup[companyId] = state);
};

export const initForm = ({ props, data, refs, state, companyId }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs, state, companyId });
    data.form = createForm({ props, data, refs, state, companyId });
    data.rules = createRules({ props, data, refs, state, companyId });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, state, companyId, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, state, companyId, isLoading });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refUserTemp.attachments.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesAttachment({ props, data, refs }),
        itemsKey: "refUserTemp.attachments"
    });
    loadRequestCheck({ props, data, refs, state, companyId }).then(page => {
        if (page.data.length > 0) {
            router.push({ name: routerPrefix });
            notification["error"]({ message: i18n.global.t("views.company.mgt.cannotCreateRequest") });
            return Promise.resolve();
        }
        loadForm({ props, data, refs, state, companyId }).then(() => {
            if (props.setAppPageHeader) {
                setAppPageHeader({
                    slots: {
                        top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                        default: () => createAppPageHeaderDefault({ props, data, refs, state, companyId })
                    }
                });
            }
        });
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs, state }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canDelete = state.conditionAction.canDelete({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    let statusRemark = null;
                    if ([undefined, "draft", "reEdit"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forRequestRemark[0].remark;
                    } else if (["waitReceiveCustoms", "waitApproveCenter", "waitDocCustoms"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forOfficeRemark[0].remark;
                    }
                    onClickSave({
                        props,
                        data,
                        refs,
                        formParams: { statusRemark }
                    });
                },
                classStyle: "m-1"
            })
        );
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && canDelete) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const onWebsocketConnect = ({ props, data, state }) => {
    state.websocketSubscribes.push(
        websocket.subscribe(`/user/topic/linkage/get/data`, res => {
            mapLinkageGetData({ data, key: "refUserTemp", linkageRes: res });
            if (data.linkageReceive001 && data.linkageReceive009) {
                onLinkIdCard({ props, data, isLoading })(data.linkIdCard);
            }
            setIsLoadingApp(false);
        })
    );
};

export const stateGroup = [];
